import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, timer, Subscription, throwError, tap, catchError, retry } from 'rxjs';
import { environment } from '@uw-env/environment';
import { RecaptchaService } from './recaptcha.service';
import {
  SessionError,
  SessionErrorCode,
  SessionStatus,
  SessionEvent
} from '../models/session-error.model';

/**
 * Session Manager Service
 * Manages user session throughout their visit with automatic refresh capabilities
 * Includes comprehensive error handling and automatic recovery
 */
@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {
  private readonly SESSION_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly REFRESH_BEFORE_EXPIRY = 30 * 1000; // 30 seconds before expiry
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds between retries
  private readonly baseApiUrl = environment.apiURL;

  private sessionExpiryTime: number | null = null;
  private refreshTimer: Subscription | null = null;
  private sessionInitialized = false;
  private retryCount = 0;
  private isRefreshing = false;
  private sessionUseRecaptcha = false;

  // Observable to notify components about session events
  private sessionEventSubject = new Subject<SessionEvent>();
  public sessionEvent$ = this.sessionEventSubject.asObservable();

  // Backwards compatibility - status only
  public sessionStatus$ = new Observable<SessionStatus>((observer) => {
    return this.sessionEvent$.subscribe((event) => observer.next(event.status));
  });

  constructor(
    private http: HttpClient,
    private recaptchaService: RecaptchaService
  ) {}

  /**
   * Initialize session when user lands on donation page
   * For light campaigns, calls backend with reCAPTCHA proof to get session ID
   * For regular campaigns, calls backend directly without reCAPTCHA
   * Includes automatic retry and error recovery
   * @param useRecaptcha Whether to use reCAPTCHA token (true for light campaigns only)
   * @returns Observable with session initialization result
   */
  initializeSession(useRecaptcha: boolean = false): Observable<any> {
    this.emitEvent('initializing');

    // If already refreshing, wait for it to complete
    if (this.isRefreshing) {
      return this.waitForRefresh();
    }

    this.isRefreshing = true;
    this.sessionUseRecaptcha = useRecaptcha;

    if (!useRecaptcha) {
      return this.callBackendForSession(null);
    }

    return new Observable((observer) => {
      this.recaptchaService.execute('session_init').subscribe(
        (recaptchaToken: string) => {
          this.callBackendForSession(recaptchaToken).subscribe(
            (response) => {
              this.retryCount = 0; // Reset retry count on success
              observer.next(response);
              observer.complete();
            },
            (error) => {
              const sessionError = this.handleSessionError(error);
              observer.error(sessionError);
            }
          );
        },
        (error) => {
          console.error('SessionManager: Failed to get reCAPTCHA token', error);
          const sessionError = this.createError(
            SessionErrorCode.VERIFICATION_FAILED,
            'Failed to verify reCAPTCHA. Please try again.',
            false,
            error
          );
          this.isRefreshing = false;
          this.emitEvent('error', sessionError);
          observer.error(sessionError);
        }
      );
    });
  }

  /**
   * Call backend to get session, optionally with reCAPTCHA proof
   * Backend returns session cookie which browser automatically stores
   * Includes retry logic for network errors
   * @param recaptchaToken The reCAPTCHA token proof, or null for regular campaigns
   * @returns Observable with session data
   */
  private callBackendForSession(recaptchaToken: string | null): Observable<any> {
    const url = `${this.baseApiUrl}/session/init`;
    const body = recaptchaToken ? { recaptchaToken } : {};

    return this.http.post(url, body, { withCredentials: true }).pipe(
      retry({
        count: this.MAX_RETRY_ATTEMPTS,
        delay: (error, retryCount) => {
          const sessionError = this.parseBackendError(error);
          if (!sessionError.canRetry) {
            throw error;
          }
          return timer(this.RETRY_DELAY);
        }
      }),
      tap((_response: any) => {
        this.setSessionExpiry();
        this.scheduleRefresh();
        this.sessionInitialized = true;
        this.isRefreshing = false;
        this.emitEvent('active');
      }),
      catchError((error) => {
        console.error('SessionManager: Failed to initialize session', error);
        this.isRefreshing = false;
        return throwError(() => error);
      })
    );
  }

  /**
   * Set the session expiry time (5 minutes from now)
   */
  private setSessionExpiry(): void {
    this.sessionExpiryTime = Date.now() + this.SESSION_DURATION;
  }

  /**
   * Schedule automatic refresh 30 seconds before session expires
   */
  private scheduleRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    const refreshTime = this.SESSION_DURATION - this.REFRESH_BEFORE_EXPIRY;

    this.refreshTimer = timer(refreshTime).subscribe(() => {
      this.refreshSession(true).subscribe(
        () => {
        },
        (error) => {
          console.error('SessionManager: Auto-refresh failed', error);
          // Error already handled in refreshSession
        }
      );
    });
  }

  /**
   * Refresh the session by getting a new session ID
   * Automatically called 30 seconds before expiry
   * Includes automatic retry on failure
   * @param isAutoRefresh - Whether this is an automatic refresh (default: false)
   * @returns Observable with refresh result
   */
  refreshSession(isAutoRefresh: boolean = false): Observable<any> {
    if (this.isRefreshing) {
      return this.waitForRefresh();
    }

    this.isRefreshing = true;
    this.emitEvent('refreshing');

    if (!this.sessionUseRecaptcha) {
      return this.callBackendForSession(null);
    }

    return new Observable((observer) => {
      this.recaptchaService.execute('session_refresh').subscribe(
        (recaptchaToken: string) => {
          this.callBackendForSession(recaptchaToken).subscribe(
            (response) => {
              observer.next(response);
              observer.complete();
            },
            (error) => {
              console.error('SessionManager: Failed to refresh session', error);
              const sessionError = this.handleSessionError(error);

              // If refresh fails, try to initialize new session
              if (sessionError.canRetry && this.retryCount < this.MAX_RETRY_ATTEMPTS) {
                this.retryCount++;

                this.initializeSession(this.sessionUseRecaptcha).subscribe(
                  (initResponse) => {
                    observer.next(initResponse);
                    observer.complete();
                  },
                  (initError) => {
                    this.handleSessionExpiry();
                    observer.error(initError);
                  }
                );
              } else {
                this.handleSessionExpiry();
                observer.error(sessionError);
              }
            }
          );
        },
        (error) => {
          console.error('SessionManager: Failed to get reCAPTCHA token for refresh', error);
          const sessionError = this.createError(
            SessionErrorCode.VERIFICATION_FAILED,
            'Failed to verify reCAPTCHA for session refresh.',
            true,
            error
          );
          this.isRefreshing = false;
          this.emitEvent('error', sessionError);
          observer.error(sessionError);
        }
      );
    });
  }

  /**
   * Check if current session is still valid
   * Automatically attempts to recover if session is invalid
   * @param autoRecover - Whether to automatically initialize new session if invalid (default: true)
   * @returns true if session is valid, false otherwise
   */
  isSessionValid(autoRecover: boolean = true): boolean {
    if (!this.sessionInitialized || !this.sessionExpiryTime) {
      if (autoRecover) {
        this.initializeSession(this.sessionUseRecaptcha).subscribe(
          () => {},
          (error) => console.error('SessionManager: Auto-recovery failed', error)
        );
      }
      return false;
    }

    const isValid = Date.now() < this.sessionExpiryTime;

    if (!isValid) {
      console.warn('SessionManager: Session has expired');

      if (autoRecover) {
        this.refreshSession().subscribe(
          () => {},
          (error) => console.error('SessionManager: Auto-recovery failed', error)
        );
      } else {
        this.handleSessionExpiry();
      }
    }

    return isValid;
  }

  /**
   * Get remaining time in session (in seconds)
   * @returns seconds remaining or 0 if expired/not initialized
   */
  getSessionTimeRemaining(): number {
    if (!this.sessionInitialized || !this.sessionExpiryTime) {
      return 0;
    }

    const remaining = Math.max(0, Math.floor((this.sessionExpiryTime - Date.now()) / 1000));
    return remaining;
  }

  /**
   * Handle session expiry
   * Cleans up timers and notifies subscribers
   */
  private handleSessionExpiry(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = null;
    }

    this.sessionExpiryTime = null;
    this.sessionInitialized = false;
    this.isRefreshing = false;

    const error = this.createError(
      SessionErrorCode.SESSION_EXPIRED,
      'Session has expired. A new session will be created automatically.',
      true
    );

    this.emitEvent('expired', error);
  }

  /**
   * Parse backend error response and create SessionError
   * @param error The HTTP error response
   * @returns Structured SessionError object
   */
  private parseBackendError(error: any): SessionError {
    // Handle HTTP error responses
    if (error instanceof HttpErrorResponse) {
      const errorBody = error.error;

      // Check for error code in response body
      if (errorBody && errorBody.errorCode) {
        return this.mapErrorCode(errorBody.errorCode, errorBody.message);
      }

      // Check for error code in error object
      if (errorBody && errorBody.code) {
        return this.mapErrorCode(errorBody.code, errorBody.message);
      }

      // HTTP status-based error mapping
      switch (error.status) {
        case 401:
          return this.createError(
            SessionErrorCode.INVALID_SESSION,
            'Session is invalid. Getting a new session...',
            true,
            error
          );

        case 403:
          return this.createError(
            SessionErrorCode.VERIFICATION_FAILED,
            'Security verification failed. Please try again.',
            false,
            error
          );

        case 429:
          return this.createError(
            SessionErrorCode.TOO_MANY_REQUESTS,
            'Too many requests. Please wait a moment before trying again.',
            false,
            error
          );

        case 0:
        case 502:
        case 503:
        case 504:
          return this.createError(
            SessionErrorCode.NETWORK_ERROR,
            'Network connection issue. Retrying...',
            true,
            error
          );

        default:
          return this.createError(
            SessionErrorCode.UNKNOWN_ERROR,
            'An unexpected error occurred. Please try again.',
            true,
            error
          );
      }
    }

    // Handle non-HTTP errors
    return this.createError(
      SessionErrorCode.UNKNOWN_ERROR,
      'An unexpected error occurred.',
      false,
      error
    );
  }

  /**
   * Map backend error code string to SessionError
   * @param errorCode The error code from backend
   * @param message Optional message from backend
   * @returns SessionError object
   */
  private mapErrorCode(errorCode: string, message?: string): SessionError {
    switch (errorCode.toLowerCase()) {
      case 'no_session':
        return this.createError(
          SessionErrorCode.NO_SESSION,
          message || 'No active session. Initializing new session...',
          true
        );

      case 'invalid_session':
        return this.createError(
          SessionErrorCode.INVALID_SESSION,
          message || 'Session is invalid. Getting a new session...',
          true
        );

      case 'session_expired':
        return this.createError(
          SessionErrorCode.SESSION_EXPIRED,
          message || 'Session has expired. Refreshing session...',
          true
        );

      case 'verification_failed':
        return this.createError(
          SessionErrorCode.VERIFICATION_FAILED,
          message || 'Security verification failed. Please try again.',
          false
        );

      case 'too_many_requests':
      case 'rate_limit_exceeded':
        return this.createError(
          SessionErrorCode.TOO_MANY_REQUESTS,
          message || 'Too many requests. Please wait a moment before trying again.',
          false
        );

      default:
        return this.createError(
          SessionErrorCode.UNKNOWN_ERROR,
          message || 'An error occurred. Please try again.',
          true
        );
    }
  }

  /**
   * Create a structured SessionError object
   * @param code The error code
   * @param userMessage User-friendly message
   * @param canRetry Whether the error is recoverable
   * @param technicalDetails Optional technical details
   * @returns SessionError object
   */
  private createError(
    code: SessionErrorCode,
    userMessage: string,
    canRetry: boolean,
    technicalDetails?: any
  ): SessionError {
    return {
      code,
      message: this.getErrorMessage(code),
      userMessage,
      canRetry,
      technicalDetails
    };
  }

  /**
   * Get standard error message for error code
   * @param code The error code
   * @returns Standard error message
   */
  private getErrorMessage(code: SessionErrorCode): string {
    const messages: Record<SessionErrorCode, string> = {
      [SessionErrorCode.NO_SESSION]: 'No active session found',
      [SessionErrorCode.INVALID_SESSION]: 'Session cookie is invalid',
      [SessionErrorCode.SESSION_EXPIRED]: 'Session has expired',
      [SessionErrorCode.VERIFICATION_FAILED]: 'reCAPTCHA verification failed',
      [SessionErrorCode.TOO_MANY_REQUESTS]: 'Rate limit exceeded',
      [SessionErrorCode.NETWORK_ERROR]: 'Network connection error',
      [SessionErrorCode.UNKNOWN_ERROR]: 'Unknown error occurred'
    };

    return messages[code] || 'Unknown error';
  }

  /**
   * Handle session error and decide recovery strategy
   * @param error The error to handle
   * @returns SessionError with recovery info
   */
  private handleSessionError(error: any): SessionError {
    const sessionError = this.parseBackendError(error);

    // Emit error event
    this.emitEvent('error', sessionError);

    // Automatic recovery for certain error types
    if (sessionError.canRetry) {
      switch (sessionError.code) {
        case SessionErrorCode.NO_SESSION:
        case SessionErrorCode.INVALID_SESSION:
        case SessionErrorCode.SESSION_EXPIRED:
          break;

        case SessionErrorCode.NETWORK_ERROR:
          break;
      }
    }

    return sessionError;
  }

  /**
   * Emit session event to subscribers
   * @param status Current session status
   * @param error Optional error information
   */
  private emitEvent(status: SessionStatus, error?: SessionError): void {
    const event: SessionEvent = {
      status,
      error,
      timestamp: Date.now()
    };

    this.sessionEventSubject.next(event);
  }

  /**
   * Wait for ongoing refresh to complete
   * @returns Observable that completes when refresh is done
   */
  private waitForRefresh(): Observable<any> {
    return new Observable((observer) => {
      const subscription = this.sessionEvent$.subscribe((event) => {
        if (event.status === 'active') {
          observer.next(undefined);
          observer.complete();
          subscription.unsubscribe();
        } else if (event.status === 'error' || event.status === 'expired') {
          observer.error(event.error);
          subscription.unsubscribe();
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        observer.error(this.createError(
          SessionErrorCode.UNKNOWN_ERROR,
          'Session refresh timeout',
          true
        ));
        subscription.unsubscribe();
      }, 10000);
    });
  }

  /**
   * Manually destroy session and clean up resources
   * Use when user navigates away or component is destroyed
   */
  destroySession(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = null;
    }

    this.sessionExpiryTime = null;
    this.sessionInitialized = false;
    this.isRefreshing = false;
    this.retryCount = 0;
  }

  /**
   * Check if session is initialized
   * @returns true if session has been initialized
   */
  isSessionInitialized(): boolean {
    return this.sessionInitialized;
  }

  /**
   * Get the last session error if any
   * Useful for displaying error messages to users
   * @returns SessionError or null
   */
  getLastError(): SessionError | null {
    let lastError: SessionError | null = null;

    this.sessionEvent$.subscribe((event) => {
      if (event.error) {
        lastError = event.error;
      }
    });

    return lastError;
  }

  /**
   * Ensure session is valid before performing an action
   * Automatically recovers if session is invalid
   * @returns Observable that completes when session is ready
   */
  ensureSession(): Observable<void> {
    return new Observable((observer) => {
      if (this.isSessionValid(false)) {
        // Session is valid
        observer.next();
        observer.complete();
      } else {
        // Session invalid, initialize or refresh

        const operation = this.sessionInitialized
          ? this.refreshSession()
          : this.initializeSession(this.sessionUseRecaptcha);

        operation.subscribe(
          () => {
            observer.next(undefined);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
      }
    });
  }
}
